import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { EMAIL, TRADES } from "@/lib/data";

const SCALES = ["Residential", "Commercial", "Industrial"];
const URGENCY = ["Standard", "Priority", "Emergency"];

export default function BriefBuilder() {
    const [code, setCode] = useState(TRADES[0].code);
    const [scale, setScale] = useState("Residential");
    const [urgency, setUrgency] = useState("Standard");

    const trade = TRADES.find((t) => t.code === code);

    const href = `mailto:${EMAIL}?subject=${encodeURIComponent(
        `Quote request — ${trade.code} ${trade.title}`,
    )}&body=${encodeURIComponent(
        `Hello,\n\nI'd like a quote for the following:\n\nService: ${trade.code} — ${trade.title}\nScope: ${scale}\nUrgency: ${urgency}\n\nDetails:\n\n`,
    )}`;

    const Option = ({ active, children, onClick, testid }) => (
        <button
            type="button"
            data-testid={testid}
            aria-pressed={active}
            onClick={onClick}
            className={`rounded-full border px-4 py-2 font-display text-sm font-medium transition-all duration-200 ${
                active
                    ? "border-amber-400/70 bg-amber-400/10 text-amber-300"
                    : "border-white/12 text-slate-300 hover:border-white/30 hover:text-white"
            }`}
        >
            {children}
        </button>
    );

    return (
        <FadeUp>
            <div className="rounded-2xl border border-white/10 bg-panel/60 p-6 md:p-10">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
                    <div className="space-y-8">
                        <div data-testid="estimator-trade-select">
                            <p className="field-label">01 · Which trade?</p>
                            <div className="flex flex-wrap gap-2">
                                {TRADES.map((t) => (
                                    <Option
                                        key={t.code}
                                        testid={`estimator-trade-option-${t.code}`}
                                        active={code === t.code}
                                        onClick={() => setCode(t.code)}
                                    >
                                        <span className="font-mono text-[11px] text-amber-400/80">
                                            {t.code}
                                        </span>
                                        <span className="ml-2">{t.short || t.title.split(",")[0].split(" & ")[0]}</span>
                                    </Option>
                                ))}
                            </div>
                        </div>
                        <div data-testid="estimator-scale-select">
                            <p className="field-label">02 · Scope scale</p>
                            <div className="flex flex-wrap gap-2">
                                {SCALES.map((s) => (
                                    <Option
                                        key={s}
                                        testid={`estimator-scale-option-${s.toLowerCase()}`}
                                        active={scale === s}
                                        onClick={() => setScale(s)}
                                    >
                                        {s}
                                    </Option>
                                ))}
                            </div>
                        </div>
                        <div data-testid="estimator-urgency-select">
                            <p className="field-label">03 · How urgent?</p>
                            <div className="flex flex-wrap gap-2">
                                {URGENCY.map((u) => (
                                    <Option
                                        key={u}
                                        testid={`estimator-urgency-option-${u.toLowerCase()}`}
                                        active={urgency === u}
                                        onClick={() => setUrgency(u)}
                                    >
                                        {u}
                                    </Option>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col justify-between gap-6 rounded-xl border border-white/10 bg-[#0E1523] p-6">
                        <div>
                            <p className="field-label">Your brief</p>
                            <p
                                data-testid="estimator-summary"
                                className="font-mono text-[13px] leading-relaxed text-slate-300"
                            >
                                <span style={{ color: trade.accent }}>{trade.code}</span>
                                {" · "}
                                {trade.title}
                                <br />
                                {scale} scope · {urgency.toLowerCase()} request
                            </p>
                        </div>
                        <div className="space-y-3">
                            <a
                                data-testid="estimator-calculate-quote-button"
                                href={href}
                                className="btn-primary w-full"
                            >
                                <Mail size={16} />
                                Send brief by email
                            </a>
                            <Link
                                to="/contact"
                                className="flex items-center justify-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 transition-colors hover:text-amber-400"
                            >
                                Prefer a form? Use contact
                                <ArrowUpRight size={12} />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </FadeUp>
    );
}
